import { useState, useEffect, useRef } from "react";
import { Form, Select, InputNumber, Input } from "antd";
import LoadingButton from '@mui/lab/LoadingButton';
import { CheckCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { backendGetRequest, backendPostWithoutRefresh } from "../../../../helpers/callBackend";
import GenericModal from "../../../buttons/genericModal";
import GenerateCode from "../../../formItems/generateCode";
import { errorMessage } from "../../../../helpers/errorMessageFunc";
import { currencyParser, currencyFormatter } from "../../../formItems/dollarAmount";
import { useUcs } from "../../../../helpers/contexts/UserCompanySettingsContext";
import checkFlag from "../../../../helpers/featureFlags";
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'


const { Option } = Select;

const InitiateInternal = (props) => {
  const [internalAccounts, setInternalAccounts] = useState([]);
  const [verificationToken, setVerificationToken] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [inFlight, setInFlight] = useState(false)
  const formRef = useRef(null);
  const ucsContext = useUcs()

  const fetchData = async () => {
    let params = {
      sources: "UN"
    }
    if (props.bankAccountSource !== "UN") {
      params = {
        payment_verified: true
      }
    }
    try {
      backendGetRequest(
        "/api/bank-account/",
        {
          params: {
            ...params,
            quick_get: true,
            page_index: 0,
          },
        }).then((response) => {
          for (var i = 1; i <= response?.data?.total_pages; i++) {
            backendGetRequest(
              "/api/bank-account/",
              {
                params: {
                  ...params,
                  quick_get: true,
                  page_index: i,
                },
              }).then((response) => {
                setInternalAccounts((prevState) => [
                  ...(prevState || []),
                  ...response?.data?.data,
                ]);
              });
          }
        });
    } catch (e) {
      console.error('could not fetch data', e);
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleVerificatonCode = (values) => {
    setVerificationToken(values.verificationToken);
  };

  const onFormSubmitMakeBookTransferPayModal = (values) => {
    if (props.availableBalance < values.amount) {
      errorMessage(
        'Insufficient funds. Amount exceeds the available balance.'
      );
      return;
    }

    if (!ucsContext.loaded) {
      errorMessage('Could not load your user permissions.')
      return
    }
    if (props.bankAccountSource !== "UN") {
      createPaymentRequest(values)
      return
    }

    const data = {
      ...values,
      bank_account_id: props.bankAccountId,
      idempotencyKey: idempotencyKey,
      verificationToken: verificationToken
    };
    // check self-approve limit for creating payments from unit accounts
    if (!ucsContext.checkLimit('self-approve', data.amount)) {
      errorMessage(
        `Amount of $${data.amount} exceeds your payment self-approval limit of $${ucsContext.selfApproveLimit()}`
      )
      return
    }

    setInFlight(true);
    backendPostWithoutRefresh("/api/payments/book-transfer/", data, false, false)
      .then((response) => {
        props.updateTransactions(response.data.transaction);
        setIdempotencyKey(uuidv4());
        if (formRef.current) {
          formRef.current.resetFields();
          setInFlight(false);
          setCurrent(1);
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        setInFlight(false);
      });

  }

  const createPaymentRequest = async (values) => {
    const data = {
      bankAccountId: props.bankAccountId,
      to_bank_account_id: values?.receiving_bank_account_id,
      amount: values?.amount,
      description: values?.description,
      idempotencyKey: idempotencyKey,
      otp: values?.verificationCode,
    }

    if (!ucsContext.checkLimit('create', data.amount)) {
      errorMessage(
        `${data.amount} exceeds your payment request creation limit of ${ucsContext.createLimit()}`
      )
      return
    }

    if (!data.otp) {
      errorMessage('No code provided, Please input the 6-digit code sent to your phone in order to initiate internal payment request')
      return
    }

    setInFlight(true)
    try {
      const paymentRequestResp = await backendPostWithoutRefresh('/api/payments/payment-request/', data)

      props.updatePaymentRequests(paymentRequestResp.data?.data, "add")
    } catch (error) {
      console.error(error)
      setInFlight(false)
      return
    }
    setInFlight(false)
    formRef.current.resetFields()
    setCurrent(1)
  }

  const accountDescriptor = (acct) => {
    return `${acct.beneficiary.name} / ${acct.account.account_description} / ${accountBalance(acct)}`
  }

  const accountBalance = (acct) => {
    return acct.account.available_balance
  }

  return (
    <GenericModal
      size='md'
      visible={visible}
      handleCancelModal={() => {
        if (formRef.current) {
          formRef.current.resetFields();
        }
        setVisible(false)
        if (current === 1) {
          setCurrent(0)
        }
      }}
      showModal={() => setVisible(true)}
      button={<span>Internal Transfer</span>}
      modalTitle={'Transfer to Internal Accounts'}
      modalBody={
        current === 0 ? (
          <Stack spacing={2}>
            <Form
              layout="vertical"
              name="basic"
              onFinish={onFormSubmitMakeBookTransferPayModal}
              autoComplete="off"
              ref={formRef}
            >
              <Form.Item
                label="Amount"
                name="amount"
                rules={[
                  { required: true, message: "Please input total Amount!" },
                ]}
              >
                <InputNumber
                  formatter={value => currencyFormatter(value)}
                  parser={value => currencyParser(value)}
                  prefix="$"
                  min={1}
                  max={100000000000000}
                  size={"large"}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item label="Receiving Account" name="receiving_bank_account_id">
                <Select
                  placeholder="Select a bank account"
                  allowClear
                  showSearch
                  dropdownMatchSelectWidth={false}
                  optionFilterProp="children"
                >
                  {internalAccounts.map((account) => (
                    <Option key={account.id} value={account.id}>
                      {accountDescriptor(account)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input />
              </Form.Item>
              {(checkFlag('otpPayments') || props.bankAccountSource === "UN") && (
                <GenerateCode
                  bankAccountId={props.bankAccountId}
                  onChange={handleVerificatonCode}
                  bankAccountSource={props.bankAccountSource}
                />
              )}
              <Form.Item>
                <LoadingButton
                  htmlType="submit"
                  style={{ margin: "3px" }}
                  loading={inFlight}
                  loadingPosition="start"
                  sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                  disableElevation fullWidth size="small" type="submit" variant="contained" color="primary"
                >
                  Submit
                </LoadingButton>
              </Form.Item>
            </Form>
            {checkFlag('paymentsV2') && props.bankAccountSource !== "UN" && (
              <Typography fontStyle={'italic'} color={'text.disabled'}>
                Payment will not automatically send. Your payment will need to be approved by a teammate before it gets sent.
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={5} alignItems='center'>
            <CheckCircleOutlined style={{ color: 'green', fontSize: '30px' }} />
            {props.bankAccountSource === 'UN' ? (
              <Typography color='green' sx={{ fontSize: '20px', marginTop: '10px' }}>
                Payment successfully submitted
              </Typography>
            ) : (
              <Stack spacing={1} alignItems={"center"}>
                <Typography color='green' sx={{ fontSize: '20px', marginTop: '10px' }}>
                  Payment request successfully created
                </Typography>
                <Typography fontStyle={'italic'} color={'text.disabled'}>
                  Note: Payment will not send until the request is approved.
                </Typography>
              </Stack>
            )}
          </Stack>
        )
      } />
  );
}

export default InitiateInternal;